'use client';

import {
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetPortal,
  Typography,
} from '@musinsa/mds';
import { HtmlContentRenderer } from '../../../../agreements/components/HtmlContentRenderer';
import { useStaticHtmlContent } from '../../../../agreements/hooks/useStaticHtmlContent';
import { usePreventBodyScroll } from '../../../../common/hooks/usePreventBodyScroll';
import {
  getSeedingTermHtmlPath,
  SEEDING_TERM_HTML_CLASS,
  SEEDING_TERMS,
  type SeedingTermId,
} from './seedingTerms';

interface SeedingTermsSheetProps {
  /** 열린 약관 id (null 이면 시트 닫힘). */
  openTermId: SeedingTermId | null;
  onClose: () => void;
}

/**
 * 시딩 약관 본문 풀스크린 시트 — `AgreementModal` Sheet 패턴(controlled).
 * 정적 HTML(`public/html/seeding/*`)을 `useStaticHtmlContent` 로 받아 DOMPurify 렌더.
 */
export const SeedingTermsSheet = ({ openTermId, onClose }: SeedingTermsSheetProps) => {
  const term = SEEDING_TERMS.find((item) => item.id === openTermId) ?? null;
  const open = term !== null;

  usePreventBodyScroll({ open });

  const { htmlContent, isLoading, error } = useStaticHtmlContent(
    term ? getSeedingTermHtmlPath(term.htmlFile) : '',
    { enabled: open },
  );

  return (
    <Sheet
      direction="bottom"
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetPortal>
        <SheetContent className="flex h-full w-screen flex-col bg-white">
          <BottomSheetTitle>
            <BottomSheetTitleContainer className="px-4 py-3">
              <BottomSheetTitleBox>
                <Typography size="16" weight="medium">
                  {term?.sheetTitle ?? ''}
                </Typography>
              </BottomSheetTitleBox>
              <IconButton size="size28x28" onClick={onClose}>
                <IcDelete />
              </IconButton>
            </BottomSheetTitleContainer>
          </BottomSheetTitle>

          <div className="h-full overflow-y-auto bg-white">
            <div className="px-4 py-3">
              {isLoading && (
                <Typography variant="body13pxReg" colorScheme="gray-600">
                  약관을 불러오는 중입니다.
                </Typography>
              )}
              {error && !isLoading && (
                <Typography variant="body13pxReg" colorScheme="gray-600">
                  약관을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                </Typography>
              )}
              {!isLoading && !error && htmlContent && (
                <HtmlContentRenderer html={htmlContent} className={SEEDING_TERM_HTML_CLASS} />
              )}
            </div>
          </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
};
